import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import { CreateOrUpdatePaymentMethodPaperChequeInput } from '@optimus/models';
import { useState } from 'react';
import {
  FormProvider, useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectPaperCheque, UpdatePaperChequeState } from '../../../store/slices/dashboardSlice';
import { PaymentMethodStatus, PayoutDialogContentType } from '../../../common/enums';
import { getFullAddressUS, sanitizeString } from '../../../common/utils/formUtils';
import { AddressAutoCompleteInput } from '../../../common/AddressAutoCompleteInput';
import { getPaymentMethods } from '../../../store/thunks/dashboardThunk';
import { setPaymentMethod } from '../../../api/dashboardAPI';
import { selectMemberInformation } from '../../../store/slices/onboardingSlice';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { PaymentMethodType } from '../../../common/models/PaymentMethod';
import { StateAbbreviationsUS } from '../../../common/StateAbbreviationsUS';
import { ErrorAlert, IPaperCheckFormInputs } from '../../../common/StyledFormInputs';
import {
  FieldBox,
  StyledTextField,
  PayoutCTA,
  TransparentPayoutCTA,
  RequiredMark,
} from './Common';

interface PaperCheckDetailsProps {
  setDialogSubTitle?: (event: string | JSX.Element) => void;
  setDialogContentType?: (event: PayoutDialogContentType) => void;
  setPaymentMethodId?: (event: string) => void;
  setOpenDialog?: (event: boolean) => void;
}

export function PaperCheckDetails({
  setDialogSubTitle,
  setDialogContentType,
  setPaymentMethodId,
  setOpenDialog,
}: PaperCheckDetailsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { trackPayoutSetting } = useCustomEventTracking();
  const paperCheque = useAppSelector(selectPaperCheque);
  const memberInfo = useAppSelector(selectMemberInformation);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingOnSubmit, setLoadingOnSubmit] = useState(false);
  const dispatch = useAppDispatch();

  // For UNLINKED paper checks, show empty form (like ACH behavior) (SN-1063)
  const isUnlinked = paperCheque?.status === PaymentMethodStatus.UNLINKED;

  const defaultValues = {
    paperCheckInfo: {
      businessName: isUnlinked ? '' : (sanitizeString(paperCheque?.meta?.businessName) || ''),
      businessAddress: {
        street: isUnlinked ? '' : sanitizeString(paperCheque?.meta?.addressLine1),
        city: isUnlinked ? '' : sanitizeString(paperCheque?.meta?.city),
        state: isUnlinked ? '' : sanitizeString(paperCheque?.meta?.stateProvince),
        zipcode: isUnlinked ? '' : sanitizeString(paperCheque?.meta?.postalCode),
        fullAddress: isUnlinked ? '' : getFullAddressUS(
          paperCheque?.meta?.addressLine1,
          paperCheque?.meta?.city,
          paperCheque?.meta?.stateProvince,
          paperCheque?.meta?.postalCode,
        ),
      },
    },
  };

  const methods = useForm<IPaperCheckFormInputs>({
    defaultValues,
    mode: 'onChange',
  });

  const {
    getValues, handleSubmit, register,
    formState, getFieldState,
  } = methods;

  const { submitCount } = formState;
  const addressState = getFieldState('paperCheckInfo.businessAddress', formState);
  const addressFromDatabase = submitCount > 0 || addressState.isTouched || addressState.isDirty;

  const handleSnackbarClose = () => {
    setErrorMessage('');
    setSnackbarOpen(false);
  };

  const handleRemoveAccount = () => {
    setDialogContentType?.(PayoutDialogContentType.REMOVE_ACCOUNT);
    const newSubTitle = (
      <>
        {t('wallet.payoutSettingsDialog.subTitle.delete')}
        <Typography
          variant='body4'
          component='span'
          sx={{
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.spacing(2),
            },
          }}
        >
          {`Paper Check (${paperCheque?.meta?.businessName || paperCheque?.meta?.addressLine1})?`}
        </Typography>
      </>
    );
    setPaymentMethodId?.(paperCheque?.id || '');
    setDialogSubTitle?.(newSubTitle);
  };

  const validateAddress = (street: string, city: string, zipcode: string): string | null => {
    // Address line validation: requires 1-50 characters from allowed set
    const addressPattern = /^[\sa-zA-Z0-9/\-?:().,+]{1,50}$/;
    if (street && !addressPattern.test(street)) {
      return 'Address can only contain letters, numbers, spaces, and the following: / - ? : ( ) . , +. Maximum 50 characters.';
    }

    // City validation: requires at least 1 character from allowed set
    const cityPattern = /^[\sa-zA-Z0-9/\-?:().,+]+$/;
    if (city && !cityPattern.test(city)) {
      return 'City can only contain letters, numbers, spaces, and the following: / - ? : ( ) . , +';
    }

    // Zipcode validation: 5 digits or 9 digits with dash (XXXXX or XXXXX-XXXX)
    const zipcodePattern = /^(\d{5}|\d{5}-\d{4})$/;
    if (zipcode && !zipcodePattern.test(zipcode)) {
      return 'Zipcode must be 5 digits or 9 digits (XXXXX-XXXX format).';
    }

    return null;
  };

  const onSubmit = async (data: IPaperCheckFormInputs) => {
    if (data) {
      try {
        setLoadingOnSubmit(true);

        const street = getValues('paperCheckInfo.businessAddress.street');
        const city = getValues('paperCheckInfo.businessAddress.city');
        const zipcode = getValues('paperCheckInfo.businessAddress.zipcode');

        // Validate address fields (not covered by register() validation)
        const validationError = validateAddress(street, city, zipcode);
        if (validationError) {
          setErrorMessage(validationError);
          setSnackbarOpen(true);
          setLoadingOnSubmit(false);
          return;
        }

        const request: CreateOrUpdatePaymentMethodPaperChequeInput = {
          type: 'paper_cheque',
          source: 'member',
          businessName: getValues('paperCheckInfo.businessName'),
          addressLine1: street,
          city,
          country: 'US',
          stateProvince: StateAbbreviationsUS[getValues('paperCheckInfo.businessAddress.state')],
          postalCode: zipcode,
        };

        const paymentMethod = await setPaymentMethod(memberInfo.id, request);
        const paperCheckResponse = paymentMethod.type === PaymentMethodType.PAPER_CHEQUE ?
          paymentMethod : undefined;

        if (paperCheckResponse) {
          dispatch(UpdatePaperChequeState(paperCheckResponse));
          setSnackbarOpen(false);
          setOpenDialog?.(false);
          trackPayoutSetting('Submit', 'PaperCheckForm');
          dispatch(getPaymentMethods());
        } else throw new Error('No paper check update');
      } catch (error) {
        setErrorMessage('Unable to update paper check. Please review your details');
        setSnackbarOpen(true);
      } finally {
        setLoadingOnSubmit(false);
      }
    }
  };

  return (
    <>
      <FormProvider {...methods} >
        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}>
          <Backdrop
            sx={() => ({
              color: theme.palette.common.white, zIndex: () => theme.zIndex.drawer + 1,
            })}
            open={loadingOnSubmit}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: theme.spacing(3),
              alignSelf: 'stretch',
              width: '550px',
              [theme.breakpoints.down('sm')]: {
                width: 'auto',
              },
            }}
          >
            <FieldBox name={t('wallet.payoutSettingsDialog.paperCheck.businessName')}>
              <Box sx={{ width: '100%' }}>
                <StyledTextField
                  placeholder={t('wallet.payoutSettingsDialog.paperCheck.businessName') as string}
                  {...register('paperCheckInfo.businessName', {
                    required: 'Business name is required',
                    pattern: {
                      value: /^[\sa-zA-Z0-9/\-?:().,+']+$/,
                      message: 'Business name can only contain letters, numbers, spaces, and the following: / - ? : ( ) . , + \'',
                    },
                  })}
                  sx={{
                    marginBottom: '0.5rem',
                  }}
                />
                {(formState.errors?.paperCheckInfo?.businessName) && (
                  <ErrorAlert
                    errorMessage={formState.errors.paperCheckInfo.businessName.message || 'Invalid business name'}
                  />
                )}
              </Box>
            </FieldBox>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: theme.spacing(0.75),
                flex: ' 1 0 0',
              }}
            >
              <Typography variant='h6'>
                {t('wallet.payoutSettingsDialog.paperCheck.businessAddress')}
                <RequiredMark />
              </Typography>
              <Box sx={{ width: '100%' }}>
                <AddressAutoCompleteInput
                  formFieldName={'paperCheckInfo.businessAddress'}
                  placeholderText={t('wallet.payoutSettingsDialog.paperCheck.businessAddress') as string}
                  optionalStyles={{
                    marginBottom: '0.5rem',
                  }}
                />
                {(formState.errors?.paperCheckInfo?.businessAddress) && <ErrorAlert
                  errorMessage={
                    (addressFromDatabase) ?
                      `${t('error.addressField.required')}` :
                      `${t('error.addressField.confirm')}`
                  }
                />}
              </Box>
            </Box>

            <PayoutCTA
              text={isUnlinked ? t('button.connectPaperCheck') : t('button.updatePaperCheck')}
              disable={!formState?.isValid || loadingOnSubmit}
            />

            {/* Hide "Remove Account" button for UNLINKED paper checks (SN-1063) */}
            {!isUnlinked && (
              <TransparentPayoutCTA
                text={t('button.removeAccount')}
                onClick={handleRemoveAccount}
                disable={loadingOnSubmit}
              />
            )}
          </Box>
        </form>
      </FormProvider>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
